import committedArena from "../world/arena.v1.json";
import { parseArenaDefinition, type ArenaMotionDefinition } from "../world/ArenaDefinition.js";

/**
 * Deterministic hazard-baffle timing (LOCKED-DESIGN 3.2, 18.7 rider #2).
 *
 * The hazard receiver's "one slow seeded baffle" is kernel state: a rotating
 * bar whose pose is a pure function of the simulation tick and the versioned
 * seeded gameplay stream. Nothing here reads a clock, the renderer, or the
 * cosmetic RNG, so replay validation reproduces the exact same gate schedule
 * from the seed alone.
 *
 * Parameter sources — chosen numbers and rationale:
 * - Base cycle: the authored `hazard-baffle.motion` field in arena.v1.json
 *   (axis "yaw"). Yaw advances `amplitude / tickDivisor` radians per tick —
 *   with the authored 1/180 that is a full rotation every ~18.8s, the same
 *   "slow" rate the old decorative spin displayed. The field is validated by
 *   ArenaDefinition; it is the real parameter source, not decoration.
 * - The bar is symmetric, so the gate repeats every half turn: the throat is
 *   BLOCKED while the bar lies within ±BLOCK_HALF_ANGLE of crossing it
 *   (45% duty), OPEN otherwise (55%).
 * - Per-seed variation (Section 10 "seeds vary baffle timing"): a dedicated
 *   xorshift stream salted from seed.rngSeed draws a rate scale from
 *   {0.85, 1.0, 1.15} and a phase offset uniform over one gate cycle. That
 *   yields gate half-cycles of 8.2–11.1s → open windows of 4.5–6.1s and
 *   blocked windows of 3.7–5.0s: slow enough to read, long enough that the
 *   45-tick nominal transit (below) fits with seconds to spare, short enough
 *   that the ×1.5 route demands actual timing (validated by validateSeed).
 * - BAFFLE_TRANSIT_TICKS (0.75s) is the nominal throw-to-arrival time for a
 *   hazard load, matching the game's other 45-tick beats (portal arming,
 *   beam jam). The release gate and every player-facing getter evaluate the
 *   schedule at tick + transit, so displayed OPEN means "a load released
 *   right now passes the baffle" — the fairness guardrail by construction.
 *
 * Ledger decision: window transitions are deliberately NOT ledger events.
 * A 180s run has ~20-40 transitions and replay derives them all from the
 * seed, so events would bloat every bundle for zero validation value.
 * Presentation polls the Simulation getters; the only ledger surface is the
 * explicit `cause: "baffle-deflected"` on the existing receiver-jammed event.
 */

/** Blocked share of each half-turn gate cycle. */
const BLOCKED_DUTY = 0.45;
/** Bar-to-throat angle (radians, mod half turn) below which the throat is blocked. */
const BLOCK_HALF_ANGLE = (BLOCKED_DUTY * Math.PI) / 2;
/** Distinct salt so this draw never collides with other gameplay-stream uses of rngSeed. */
const BAFFLE_STREAM_SALT = 0x42414646; // "BAFF"
/** Per-seed rate variation set; gentle so every seed stays inside the locked "slow" intent. */
const RATE_SCALES = [0.85, 1, 1.15] as const;

/** Nominal hazard-load transit (release → baffle arrival) in simulation ticks. */
export const BAFFLE_TRANSIT_TICKS = 45;

/** Authored base motion for the baffle, straight from the committed arena. */
const MOTION: ArenaMotionDefinition = (() => {
  const definition = parseArenaDefinition(committedArena);
  const motion = definition.pieces.find((piece) => piece.id === "hazard-baffle")?.motion;
  if (!motion || motion.axis !== "yaw") throw new Error("The committed arena must author a yaw motion on hazard-baffle.");
  return motion;
})();

export interface BaffleParams {
  /** Authored rest yaw (radians). */
  originYaw: number;
  /** Radians of yaw advanced per simulation tick after per-seed scaling. */
  yawPerTick: number;
  /** Seeded phase offset in ticks. */
  phaseTicks: number;
}

export interface BaffleGateState {
  blocked: boolean;
  /** 0..1 through the current gate cycle: blocked span first, then open span. */
  cycleProgress: number;
  /** Ticks until the gate opens; 0 while open. */
  ticksToNextOpen: number;
  /** Ticks until the gate blocks again; 0 while blocked. */
  ticksToNextClose: number;
}

export interface BaffleTimings {
  gateCycleTicks: number;
  blockedTicks: number;
  openTicks: number;
  rotationTicks: number;
}

/** Versioned gameplay-stream derivation of a seed's baffle schedule. */
export function baffleParamsFor(rngSeed: number): BaffleParams {
  let x = (rngSeed ^ BAFFLE_STREAM_SALT) | 0;
  if (x === 0) x = 0x1f2e3d4c;
  const next = (): number => {
    x ^= x << 13; x ^= x >>> 17; x ^= x << 5; x |= 0;
    return (x >>> 0) / 4294967296;
  };
  const rateScale = RATE_SCALES[Math.floor(next() * RATE_SCALES.length)]!;
  const yawPerTick = (MOTION.amplitude * rateScale) / MOTION.tickDivisor;
  const phaseTicks = Math.floor(next() * (Math.PI / yawPerTick));
  return { originYaw: MOTION.origin, yawPerTick, phaseTicks };
}

/** Physical bar yaw; accepts fractional ticks so renderers can interpolate exactly. */
export function baffleYawAt(tickLike: number, params: BaffleParams): number {
  return params.originYaw + (tickLike + params.phaseTicks) * params.yawPerTick;
}

/** Gate state at a tick: blocked/open, cycle progress, and ticks until open. */
export function baffleGateAt(tickLike: number, params: BaffleParams): BaffleGateState {
  const barAngle = wrapHalfTurn(baffleYawAt(tickLike, params));
  // Shift so the blocked band [−φ, +φ] around the throat axis becomes [0, 2φ).
  const cursor = wrapHalfTurn(barAngle + BLOCK_HALF_ANGLE);
  const blocked = cursor < BLOCK_HALF_ANGLE * 2;
  return {
    blocked,
    cycleProgress: cursor / Math.PI,
    ticksToNextOpen: blocked ? (BLOCK_HALF_ANGLE * 2 - cursor) / params.yawPerTick : 0,
    ticksToNextClose: blocked ? 0 : (Math.PI - cursor) / params.yawPerTick,
  };
}

/** Window durations for feasibility checks and tuning review. */
export function baffleTimings(params: BaffleParams): BaffleTimings {
  const gateCycleTicks = Math.PI / params.yawPerTick;
  return {
    gateCycleTicks,
    blockedTicks: gateCycleTicks * BLOCKED_DUTY,
    openTicks: gateCycleTicks * (1 - BLOCKED_DUTY),
    rotationTicks: gateCycleTicks * 2,
  };
}

function wrapHalfTurn(angle: number): number {
  return ((angle % Math.PI) + Math.PI) % Math.PI;
}
